import { useSearchParams } from 'next/navigation';
import { type ComponentProps, type MouseEvent, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type CardLocation from '@alltrails/amplitude/enums/CardLocation';
import type ExploreMapPinType from '@alltrails/amplitude/enums/ExploreMapPinType';
import type TrailCardLocation from '@alltrails/amplitude/enums/TrailCardLocation';
import logExploreMapPinSelected from '@alltrails/amplitude/events/logExploreMapPinSelected';
import logTrailCardClicked from '@alltrails/amplitude/events/logTrailCardClicked';
import TrailCard from '@alltrails/cards/components/TrailCard';
import useAuthorization from '@alltrails/context/hooks/useAuthorization';
import Typography from '@alltrails/core/components/Typography';
import { useClientNavigationPushState } from '@alltrails/core/hooks/useClientNavigation';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import logError from '@alltrails/core/utils/logError';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import Carousel from '@alltrails/denali/components/Carousel';
import { DropdownMenuItems } from '@alltrails/denali/components/DropdownMenu';
import IconButton from '@alltrails/denali/components/IconButton';
import Check from '@alltrails/denali/icons/Check';
import Copy from '@alltrails/denali/icons/Copy';
import Edit from '@alltrails/denali/icons/Edit';
import Location from '@alltrails/denali/icons/Location';
import type TrailActivityType from '@alltrails/trails/types/TrailActivityType';
import trailFromAlgoliaTrail from '@alltrails/trails/utils/trailFromAlgoliaTrail';
import type { TrailSearchResult } from '@alltrails/types/AlgoliaResults';
import { useAppDispatch } from '../../redux';
import { setPendingFlyTo } from '../../redux/reducer';
import type { Coordinates } from '../../types/Geo';
import type { TrailheadInView } from '../../types/Trailhead';
import { pillPopupOffset } from '../../utils/svgDefinitions/svgMarkerComponents';
import PaddingAwarePopup from '../PaddingAwarePopup/PaddingAwarePopup';
import styles from './TrailheadPopup.module.scss';
import { MapLayerInteractionsContext } from '../MapLayerInteractionsProvider/MapLayerInteractionsProvider';

// The popup is the same width at every viewport, so one card fills it at all breakpoints rather than
// the carousel's default of 3 or 4 cards on wider screens.
const carouselSlidesPerViewBreakpoints = { 760: 1, 1120: 1 };

export type SelectedTrailhead = {
  name: string;
  coordinates: Coordinates;
  id: TrailheadInView['id'];
  trailIds: number[];
};

type TrailheadPopupProps = {
  trailhead: SelectedTrailhead;
  canAddToRoute?: boolean;
  activityType?: TrailActivityType; // Used to display the correct trail card stats
  cardLocation?: TrailCardLocation;
  detailedCardLocation?: CardLocation;
  inspirationalPageSlug?: string;
  initialTrailIndex?: number;
  isComplete?: (id: number) => boolean;
  isFavorite?: (id: number) => boolean;
  isVerified?: (id: number) => boolean;
  onFavoriteClick?: (id: number) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onTrailCardViewed?: (id: number, index: number) => void;
  onTrailChange: (trail: TrailSearchResult) => void;
  onTrailNavigate?: (slug: string, event?: MouseEvent) => void;
  trails: TrailSearchResult[];
  getCTAProps?: (trailId: number, trailSlug: string) => ComponentProps<typeof TrailCard>['ctaProps'];
  getTrailCardUrl?: (trailSlug: string) => string;
};

const TrailheadPopup = ({
  activityType,
  canAddToRoute = false,
  cardLocation,
  detailedCardLocation,
  getCTAProps,
  getTrailCardUrl,
  inspirationalPageSlug,
  initialTrailIndex = 0,
  isComplete,
  isFavorite,
  isVerified,
  onFavoriteClick,
  onMouseEnter,
  onMouseLeave,
  onTrailCardViewed,
  onTrailChange,
  onTrailNavigate,
  trailhead,
  trails
}: TrailheadPopupProps) => {
  const [isCopied, setIsCopied] = useState(false);
  const intl = useIntl();
  const [index, setIndex] = useState(initialTrailIndex);
  const languageRegionCode = useLanguageRegionCode();
  const { hasPermission } = useAuthorization();
  const searchParams = useSearchParams();
  const pushState = useClientNavigationPushState();
  const dispatch = useAppDispatch();
  const { setOverlayActive } = useContext(MapLayerInteractionsContext);

  useEffect(() => {
    setIndex(initialTrailIndex);
  }, [initialTrailIndex]);

  useEffect(() => {
    if (trails.length > 1) {
      // For trailheads with more than one trail, when the popup is visible, block map layer events to prevent
      //  POI pins (or other layers) from receiving hover/click events
      setOverlayActive('trailhead-popup', true);
    }

    return () => setOverlayActive('trailhead-popup', false);
  }, [setOverlayActive, trails.length]);

  const exploreUrl = useMemo(
    () => wrapUrlSafe(`/explore/trailhead/${trailhead.id}?${searchParams.toString()}`, languageRegionCode),
    [trailhead.id, languageRegionCode, searchParams]
  );

  const returnTo = useMemo(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return window.location.pathname + window.location.search;
  }, []);

  const buildCustomRouteUrl = useCallback(() => {
    const params = new URLSearchParams(searchParams.toString());
    if (trailhead.coordinates) {
      params.set('lat', trailhead?.coordinates?.lat.toString());
      params.set('lng', trailhead?.coordinates?.lng.toString());
    }
    if (returnTo) {
      params.set('returnTo', returnTo);
    }
    return wrapUrlSafe(`/explore/custom-routes/new?${params.toString()}`, languageRegionCode);
  }, [searchParams, returnTo, languageRegionCode, trailhead.coordinates]);

  const options = useMemo(
    () => [
      {
        leadingIcon: { icon: { Component: Location, size: 'xs' } },
        onClick: () => {
          if (canAddToRoute) {
          } else {
            // TODO explore trailhead view: https://alltrails.atlassian.net/browse/MAPS-236
            pushState(exploreUrl);
          }
        },
        title: <FormattedMessage defaultMessage="Show trails" />
      },
      {
        leadingIcon: { icon: { Component: Edit, size: 'xs' } },
        onClick: () => {
          if (trailhead.coordinates) {
            dispatch(setPendingFlyTo({ center: [trailhead.coordinates.lng, trailhead.coordinates.lat], zoom: 17 }));
            pushState(buildCustomRouteUrl());
          }
        },
        title: canAddToRoute ? <FormattedMessage defaultMessage="Add to route" /> : <FormattedMessage defaultMessage="Build custom route" />
      }
    ],
    [canAddToRoute, pushState, exploreUrl, trailhead.coordinates, dispatch, buildCustomRouteUrl]
  );

  const onIndexChange = useCallback(
    (newIndex: number) => {
      const newTrail = trails[newIndex];
      setIndex(newIndex);
      onTrailChange(newTrail);
      let pinType: ExploreMapPinType = 'trailhead';
      if (trails.every(t => t.is_closed)) {
        pinType = 'closed';
      } else if (isFavorite && trails.every(t => isFavorite(t.ID))) {
        pinType = 'saved';
      } else if (isComplete && trails.every(t => isComplete(t.ID))) {
        pinType = 'completed';
      }

      logExploreMapPinSelected({
        action_taken: 'toggled from trailhead pin',
        pin_type: pinType,
        trail_id: newTrail.ID
      });
    },
    [isComplete, isFavorite, onTrailChange, trails]
  );

  const getAdminCTAs = useCallback(
    (trailId: number) => {
      if (hasPermission({ permission: 'trails:manage' })) {
        return [
          {
            id: 'edit',
            label: intl.formatMessage({ defaultMessage: 'Edit' }),
            onClick: () => window.open(`/contribute/route?trail_id=${trailId}`, '_blank')
          }
        ];
      }

      return undefined;
    },
    [hasPermission, intl]
  );

  const onCardClick = useCallback(
    (trailSlug: string, result: TrailSearchResult, event?: MouseEvent) => {
      logTrailCardClicked({
        trail_id: String(result.ID),
        card_location: cardLocation,
        detailed_card_location: detailedCardLocation,
        inspirational_page_slug: inspirationalPageSlug
      });
      onTrailNavigate?.(trailSlug, event);
    },
    [cardLocation, detailedCardLocation, inspirationalPageSlug, onTrailNavigate]
  );

  const renderTrailCard = useCallback(
    (result: TrailSearchResult) => {
      const cardTrail = trailFromAlgoliaTrail(result, activityType);

      return (
        <TrailCard
          key={result.ID} // use a key to ensure the correct trail photo is used if the shown trail is changed via hovering fast enough that the TrailCard is still rendered
          adminCTAs={getCTAProps ? undefined : getAdminCTAs(result.ID)}
          ctaProps={getCTAProps ? getCTAProps(result.ID, result.slug) : undefined}
          className={styles.trailCard}
          variant="small"
          languageRegionCode={languageRegionCode}
          onCardMouseEnter={onMouseEnter}
          onCardMouseLeave={onMouseLeave}
          onFavoriteClick={onFavoriteClick ? () => onFavoriteClick(result.ID) : undefined}
          onCardClick={e => onCardClick(cardTrail.slug, result, e)}
          blockNavigation={Boolean(onTrailNavigate)}
          isFavorite={isFavorite?.(result.ID)}
          isCompleted={isComplete?.(result.ID)}
          isVerified={isVerified?.(result.ID)}
          trail={cardTrail}
          url={getTrailCardUrl?.(cardTrail.slug)}
          lazyLoadImages={true}
          openInNewTab={!onTrailNavigate}
        />
      );
    },
    [
      activityType,
      getAdminCTAs,
      getCTAProps,
      getTrailCardUrl,
      isComplete,
      isFavorite,
      isVerified,
      languageRegionCode,
      onFavoriteClick,
      onCardClick,
      onMouseEnter,
      onMouseLeave,
      onTrailNavigate
    ]
  );

  const trail = trails?.[index];

  useEffect(() => {
    onTrailCardViewed?.(trail?.ID, index);
    // Only want to log this when the trail changes
  }, [trail?.ID]);

  const onClickCopyCoordinates = useCallback(() => {
    if (!trailhead.coordinates) {
      return;
    }
    navigator.clipboard
      .writeText(`${trailhead.coordinates.lat.toFixed(4)}, ${trailhead.coordinates.lng.toFixed(4)}`)
      .then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1500);
      })
      .catch(err => {
        logError(new Error('Failed to copy coordinates to clipboard', { cause: err }));
      });
  }, [trailhead.coordinates]);

  // TODO: mobile popup

  return (
    <PaddingAwarePopup
      closeButton={false}
      closeOnClick={false}
      focusAfterOpen={false}
      latitude={trailhead.coordinates.lat}
      longitude={trailhead.coordinates.lng}
      maxWidth="327"
      offset={pillPopupOffset}
    >
      <div className={styles.container}>
        <div className={styles.trailheadInfoContainer}>
          <div className={styles.trailheadInfoHeader}>
            <Typography variant="text200bold">{trailhead.name}</Typography>
          </div>
          <div className={styles.coordinatesContainer}>
            <Typography variant="text100" color="secondary">
              {trailhead.coordinates.lat.toFixed(4)}, {trailhead.coordinates.lng.toFixed(4)}
            </Typography>
            <IconButton
              size="sm"
              variant="ghost"
              onClick={onClickCopyCoordinates}
              title={intl.formatMessage({ defaultMessage: 'Copy coordinates' })}
              icon={{ Component: isCopied ? Check : Copy, color: '--color-text-secondary' }}
            />
          </div>
        </div>
        <div className={styles.carousel}>
          <Carousel
            getItemKey={trail => `trail-${trail.ID}`}
            initialSlideIndex={index}
            items={trails}
            leftArrowClassName={styles.carouselArrowLeft}
            onSlideChange={onIndexChange}
            renderItem={renderTrailCard}
            rightArrowClassName={styles.carouselArrowRight}
            showOverflow={false}
            showPaginationDots={false}
            slidesPerViewBreakpoints={carouselSlidesPerViewBreakpoints}
            slidesPerViewMobile={1}
          />
        </div>
        <div className={styles.divider} />
        <div className={styles.dropdownMenuItems}>
          <DropdownMenuItems options={options} />
        </div>
      </div>
    </PaddingAwarePopup>
  );
};

export default TrailheadPopup;
